/**
 * BrightLabs · v2 — Full New Homepage
 * Story beats from the approved IA (Phase 1 sitemap):
 *   01 HERO · 02 SELECTED WORK · 03 WHAT WE DO · 04 WHY BRIGHTLABS
 *   05 CREATIVE & SOCIAL · 06 PERFORMANCE · 07 TECHNOLOGY · 08 FEATURED CASE
 *   09 HOW WE WORK · 10 WAYS TO WORK WITH US · 11 FINAL CTA
 *
 * Kept restrained: one gradient moment, one warm accent, editorial spacing,
 * motion only where it earns its place.
 */
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowUpRight, ArrowRight, Sparkles, Zap, Layers, TrendingUp } from 'lucide-react';
import { V2_COLORS } from './tokens';
import { V2Nav, V2Footer, PrimaryCTA, GhostCTA, V2Kicker, V2_REVEAL as REVEAL } from './V2Shared';
import { CASE_STUDIES, CASE_ORDER } from './caseStudies';
import { useDocumentMeta } from './useDocumentMeta';
import './v2.css';

/* ----------------------------------------------------------------
   HOMEPAGE
   ---------------------------------------------------------------- */
export default function V2Home() {
  useDocumentMeta(
    'BrightLabs — Modern Digital Agency · Build · Create · Grow · Automate',
    'A modern digital agency combining creative, technology and growth. We build websites, brands, custom software — and run the marketing systems beneath them. One team. One invoice.',
    '/'
  );
  const { scrollYProgress } = useScroll();
  const heroY = useTransform(scrollYProgress, [0, 0.2], [0, -80]);

  return (
    <div
      className="min-h-screen antialiased selection:bg-[#3B4DFF] selection:text-white overflow-x-hidden"
      style={{ background: V2_COLORS.ink, color: V2_COLORS.paper }}
      data-testid="v2-home"
    >
      <V2Nav scheme="dark" active="home" />
      <main>
        <Hero heroY={heroY} />
        <SelectedWork />
        <WhatWeDo />
        <WhyBrightLabs />
        <PerformanceThesis />
        <TechnologyProof />
        <FeaturedCase />
        <HowWeWork />
        <WaysToWork />
        <FinalCTA />
      </main>
      <V2Footer />
    </div>
  );
}

/* ================================================================
   01 · HERO
   ================================================================
   Above-the-fold content renders immediately — NO opacity:0 reveal
   here (that would delay FCP by ~700ms + framer-motion boot time on
   a page the user just requested). Below-the-fold sections keep the
   reveal for polish.
*/
function Hero({ heroY }) {
  return (
    <motion.section
      style={{ y: heroY }}
      className="relative pt-40 pb-32 px-6 md:px-12 lg:px-20 max-w-[1600px] mx-auto"
      data-testid="v2-home-hero"
    >
      <div className="mb-10">
        <V2Kicker num="01">A New Chapter</V2Kicker>
      </div>

      <h1 className="v2-display leading-[0.9] tracking-[-0.03em] text-balance max-w-[16ch]">
        Make your business{' '}
        <em className="not-italic" style={fraunces('impossible', true)}>impossible</em>{' '}
        to ignore.
      </h1>

      <div className="mt-14 grid md:grid-cols-[1.2fr_1fr] gap-10 items-end">
        <p className="v2-lede max-w-[52ch] text-[#CFCBBD]">
          BrightLabs is a modern digital agency. We combine{' '}
          <span className="text-white">creative, technology and growth</span> to build and grow ambitious businesses — wherever they are.
        </p>
        <div className="flex flex-wrap items-center gap-3 md:justify-end">
          <PrimaryCTA>Start a Project</PrimaryCTA>
          <GhostCTA to="#work">See Our Work</GhostCTA>
        </div>
      </div>

      <div className="mt-24 pt-6 border-t border-white/10 flex flex-wrap items-baseline gap-x-10 gap-y-4 v2-mono text-[11px] uppercase tracking-[0.28em] text-[#9C978A]">
        <span className="text-white">Build</span>
        <span className="text-white/25">·</span>
        <span className="text-white">Create</span>
        <span className="text-white/25">·</span>
        <span className="text-white">Grow</span>
        <span className="text-white/25">·</span>
        <span className="text-white">Automate</span>
      </div>
    </motion.section>
  );
}

/* ================================================================
   02 · SELECTED WORK
   ================================================================ */
function SelectedWork() {
  const works = CASE_ORDER.map((s) => CASE_STUDIES[s]);
  return (
    <section id="work" className="border-t border-white/[0.08] py-24 md:py-32 px-6 md:px-12 lg:px-20 max-w-[1600px] mx-auto" data-testid="v2-home-work">
      <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
        <div>
          <motion.div {...REVEAL}><V2Kicker num="02">Selected Work</V2Kicker></motion.div>
          <motion.h2 {...REVEAL} transition={{ ...REVEAL.transition, delay: 0.08 }} className="v2-h1 mt-4 max-w-[18ch]">
            Work that <em style={fraunces()}>earns</em> its space.
          </motion.h2>
        </div>
        <Link to="/work" data-testid="v2-home-all-work" className="v2-mono text-[11px] uppercase tracking-[0.24em] text-[#B8B4A6] hover:text-white inline-flex items-center gap-2">
          All projects <ArrowUpRight size={12} />
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-14">
        {works.map((w, i) => <WorkTile key={w.slug} w={w} i={i} />)}
      </div>
    </section>
  );
}

function WorkTile({ w, i }) {
  const card = w.card || {};
  const chips = card.chips || w.hero?.pills?.slice(0, 4) || [];
  const stats = card.stats || [];
  return (
    <motion.div {...REVEAL} transition={{ ...REVEAL.transition, delay: 0.1 + i * 0.06 }}>
      <Link to={`/work/${w.slug}`} className="group block" data-testid={`v2-home-work-${w.slug}`}>
        <div
          className="relative aspect-[4/3] rounded-sm overflow-hidden mb-5 transition-transform duration-700 will-change-transform"
          style={{ background: `radial-gradient(120% 90% at ${i % 2 ? '85% 15%' : '15% 85%'}, ${w.accent}22 0%, transparent 60%), #0F0F16` }}
        >
          <div className="absolute inset-6 md:inset-10 rounded-sm bg-[#07070C] ring-1 ring-white/10 overflow-hidden flex flex-col">
            <div className="flex items-center gap-1.5 px-3 py-2 border-b border-white/5 bg-[#07070C] shrink-0">
              <span className="w-1.5 h-1.5 rounded-full bg-white/15" />
              <span className="w-1.5 h-1.5 rounded-full bg-white/15" />
              <span className="w-1.5 h-1.5 rounded-full bg-white/15" />
              <span className="ml-3 v2-mono text-[9px] text-white/40 tracking-wider">{w.url}</span>
              <span className="ml-auto v2-mono text-[8px] tracking-wider inline-flex items-center gap-1" style={{ color: w.accent }}>
                <span className="w-1 h-1 rounded-full" style={{ background: w.accent, boxShadow: `0 0 6px ${w.accent}` }} /> LIVE
              </span>
            </div>

            <div className="relative overflow-hidden shrink-0" style={{ height: '44%' }}>
              {w.shot ? (
                <img src={w.shot} alt={`${w.name} — live site`} loading="lazy" className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-[1200ms] group-hover:scale-[1.04]" draggable={false} />
              ) : (
                <div className="absolute inset-0" style={{ background: `radial-gradient(80% 60% at 20% 30%, ${w.accent}22, transparent 60%), #0A0A0F` }} />
              )}
              <div className="absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-[#07070C] to-transparent" />
            </div>

            <div className="flex-1 min-h-0 px-5 md:px-6 pt-3.5 pb-3 flex flex-col gap-2 bg-[#07070C]">
              <div className="v2-mono text-[11px] md:text-[12px] uppercase tracking-[0.24em] font-semibold" style={{ color: w.accent }}>
                {w.discipline.slice(0, 3).join(' · ')}
              </div>
              <div className="text-[24px] md:text-[30px] leading-[0.95]" style={{ fontFamily: 'Fraunces', fontWeight: 300, fontStyle: 'italic', color: '#F5F1E8' }}>{w.name}</div>
              <div className="text-[12px] md:text-[13px] text-[#B8B4A6] leading-snug max-w-[42ch]">{w.tagline}</div>

              {chips.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {chips.slice(0, 4).map((p) => (
                    <span key={p} className="v2-mono text-[9px] uppercase tracking-[0.2em] px-2 py-[3px] rounded-full ring-1" style={{ color: w.accent, borderColor: `${w.accent}55`, background: `${w.accent}12` }}>{p}</span>
                  ))}
                </div>
              )}

              {stats.length > 0 && (
                <div className="mt-auto grid grid-cols-3 gap-3 pt-2.5 border-t border-white/[0.08]">
                  {stats.map((s) => (
                    <div key={s.k}>
                      <div className="v2-mono text-[9px] uppercase tracking-[0.22em] text-[#9C978A]">{s.k}</div>
                      <div className="mt-0.5 text-[13px] md:text-[15px] leading-none" style={{ fontFamily: 'Fraunces', fontWeight: 300, fontStyle: 'italic', color: '#F5F1E8' }}>{s.v}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-baseline justify-between gap-4">
          <div className="min-w-0">
            <h3 className="v2-h3 text-white">{w.name}</h3>
            <p className="v2-mono text-[10px] uppercase tracking-[0.24em] text-[#9C978A] mt-1.5">{w.sector}</p>
            {card.resultLine && (
              <p className="v2-mono text-[10px] uppercase tracking-[0.22em] mt-1" style={{ color: w.accent }}>{card.resultLine}</p>
            )}
          </div>
          <span className="v2-mono text-[10px] tracking-[0.24em] text-[#9C978A] shrink-0">{w.year}</span>
        </div>
      </Link>
    </motion.div>
  );
}

/* ================================================================
   03 · WHAT WE DO — the four pillars
   ================================================================ */
function WhatWeDo() {
  const pillars = [
    {
      num: 'i',   name: 'Build',    verb: 'we',
      to: '/services/websites',
      headline: 'Websites, apps and the systems beneath them.',
      services: ['Websites', 'Digital experiences', 'eCommerce', 'CRM', 'Custom software', 'Applications', 'Portals'],
      icon: Layers, accent: '#3B4DFF',
    },
    {
      num: 'ii',  name: 'Create',   verb: 'we',
      to: '/services/creative-social',
      headline: 'How brands look, sound and stay memorable.',
      services: ['Brand identity', 'Creative direction', 'Social content', 'Reels & short-form', 'Campaign creative', 'Graphic design', 'Ad creative'],
      icon: Sparkles, accent: '#E85D2F',
    },
    {
      num: 'iii', name: 'Grow',     verb: 'we',
      to: '/services/paid-growth',
      headline: 'Turn attention into revenue.',
      services: ['Meta Ads', 'Google Ads', 'Paid acquisition', 'SEO', 'Conversion optimisation', 'Landing pages', 'Attribution'],
      icon: TrendingUp, accent: '#34D399',
    },
    {
      num: 'iv',  name: 'Automate', verb: 'we',
      to: '/services/software',
      headline: 'The machinery running quietly underneath.',
      services: ['CRM automation', 'Lead workflows', 'Email + WhatsApp automation', 'AI workflows', 'Integrations', 'Internal systems'],
      icon: Zap, accent: '#7B3DFF',
    },
  ];
  return (
    <section id="services" className="border-t border-white/[0.08] py-24 md:py-32 px-6 md:px-12 lg:px-20 max-w-[1600px] mx-auto" data-testid="v2-home-what-we-do">
      <div className="mb-14 max-w-4xl">
        <motion.div {...REVEAL}><V2Kicker num="03">What we do</V2Kicker></motion.div>
        <motion.h2 {...REVEAL} transition={{ ...REVEAL.transition, delay: 0.08 }} className="v2-h1 mt-4 max-w-[20ch]">
          Four disciplines. <em style={fraunces()}>One</em> team. One invoice.
        </motion.h2>
        <motion.p {...REVEAL} transition={{ ...REVEAL.transition, delay: 0.16 }} className="v2-lede mt-6 max-w-[58ch] text-[#B8B4A6]">
          Most agencies do one. A few do two. We&rsquo;ve built ours around the four that actually move a business forward — because the answer is rarely just one of them.
        </motion.p>
      </div>

      <div className="grid md:grid-cols-2 gap-x-6 gap-y-8">
        {pillars.map((p, i) => (
          <motion.div
            key={p.name}
            {...REVEAL}
            transition={{ ...REVEAL.transition, delay: 0.05 + i * 0.05 }}
            whileHover={{ y: -4, transition: { duration: 0.35 } }}
          >
            <Link
              to={p.to}
              data-testid={`v2-pillar-${p.name.toLowerCase()}`}
              className="group relative block p-8 md:p-10 rounded-sm bg-white/[0.02] ring-1 ring-white/[0.08] hover:ring-white/25 transition-all overflow-hidden"
            >
              <div
                className="absolute -top-24 -right-24 w-64 h-64 rounded-full opacity-0 group-hover:opacity-30 transition-opacity duration-700 blur-3xl pointer-events-none"
                style={{ background: p.accent }}
              />
              <div className="relative flex items-start justify-between mb-8">
                <div>
                  <span className="v2-mono text-[11px] uppercase tracking-[0.24em] text-[#9C978A]">{p.num}</span>
                  <div className="flex items-center gap-3 mt-3">
                    <p.icon size={16} style={{ color: p.accent }} />
                    <div className="v2-mono text-[12px] uppercase tracking-[0.24em] text-white">— {p.verb} {p.name}</div>
                  </div>
                </div>
                <ArrowUpRight size={18} className="text-[#9C978A] group-hover:text-white group-hover:rotate-45 transition-all duration-500" />
              </div>
              <h3 className="v2-h2 text-[26px] md:text-[34px] max-w-[18ch] leading-[1.05]">
                <em className="not-italic" style={{ color: p.accent, fontStyle: 'italic', fontFamily: 'Fraunces', fontWeight: 300 }}>
                  {p.name}.
                </em>{' '}
                <span className="text-white">{p.headline}</span>
              </h3>
              <ul className="mt-8 flex flex-wrap gap-1.5">
                {p.services.map((s) => (
                  <li key={s} className="v2-mono text-[10.5px] uppercase tracking-[0.22em] text-[#B8B4A6] px-2.5 py-1 rounded-full ring-1 ring-white/10">{s}</li>
                ))}
              </ul>
            </Link>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

/* ================================================================
   04 · WHY BRIGHTLABS
   ================================================================ */
function WhyBrightLabs() {
  return (
    <section id="about" className="border-t border-black/[0.08] py-24 md:py-32" data-testid="v2-home-why" style={{ background: V2_COLORS.paperCream, color: V2_COLORS.ink }}>
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20">
        <motion.div {...REVEAL}><V2Kicker num="04" warm>Why BrightLabs</V2Kicker></motion.div>
        <motion.h2 {...REVEAL} transition={{ ...REVEAL.transition, delay: 0.08 }} className="v2-display mt-6 max-w-[16ch]" style={{ fontSize: 'clamp(48px,8vw,140px)', color: V2_COLORS.ink }}>
          Creative,
          <br />
          <em style={fraunces()}>technology,</em>
          <br />
          growth.
        </motion.h2>
        <motion.p {...REVEAL} transition={{ ...REVEAL.transition, delay: 0.2 }} className="v2-lede mt-12 max-w-[64ch] text-[#3F3A2E]">
          Most agencies pick a lane. We refused. Because the businesses that pull ahead are the ones that get all three right — the way they look, the systems they run on, and the demand they generate. We build teams that can hold all three at once.
        </motion.p>

        <div className="mt-20 grid md:grid-cols-3 gap-y-10 gap-x-14">
          {[
            { n: '01', h: 'Small team, senior thinking.', p: 'No junior account layers. No outsourced delivery chain. The people who scope your work are the people who ship it.' },
            { n: '02', h: 'Technology aggressively used.', p: 'Modern tooling, AI in the workflow, custom software when it helps. Efficiency you see in the pace, not the invoice.' },
            { n: '03', h: 'Commercially intelligent.', p: 'We&rsquo;ll tell you if your website is the real problem before we sell you social. Outcomes first, tactics second.' },
          ].map((c) => (
            <motion.div key={c.n} {...REVEAL} className="border-t border-black/10 pt-6">
              <div className="v2-mono text-[10px] uppercase tracking-[0.28em]" style={{ color: V2_COLORS.warm }}>{c.n}</div>
              <h3 className="v2-h3 mt-3 text-[20px]" style={{ color: V2_COLORS.ink }}>{c.h}</h3>
              <p className="mt-3 text-[15px] leading-relaxed text-[#3F3A2E]" dangerouslySetInnerHTML={{ __html: c.p }} />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ================================================================
   05 · CREATIVE & SOCIAL SHOWCASE — placeholder until real reels ready
   ================================================================ */
function CreativeShowcase() {
  return (
    <section className="border-t border-white/[0.08] py-24 md:py-32 px-6 md:px-12 lg:px-20 max-w-[1600px] mx-auto" data-testid="v2-home-creative">
      <div className="grid md:grid-cols-[1fr_1.2fr] gap-14 items-end mb-14">
        <div>
          <motion.div {...REVEAL}><V2Kicker num="05">Creative &amp; Social</V2Kicker></motion.div>
          <motion.h2 {...REVEAL} transition={{ ...REVEAL.transition, delay: 0.08 }} className="v2-h1 mt-4 max-w-[16ch]">
            We don&rsquo;t manage feeds.
            <br />
            We make brands <em style={fraunces()}>worth following</em>.
          </motion.h2>
        </div>
        <motion.p {...REVEAL} transition={{ ...REVEAL.transition, delay: 0.2 }} className="v2-lede text-[#B8B4A6] max-w-[52ch]">
          Creative direction. Brand storytelling. Reels, carousels, campaign creative and the strategy that decides what to make next. Not &ldquo;12 posts a month&rdquo; — an ongoing brand engine.
        </motion.p>
      </div>

      <motion.div {...REVEAL} className="rounded-sm ring-1 ring-white/[0.08] bg-white/[0.02] p-10 md:p-14 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div className="max-w-[52ch]">
          <div className="v2-mono text-[10px] uppercase tracking-[0.28em]" style={{ color: '#E85D2F' }}>— Coming soon</div>
          <div className="v2-h2 mt-4 text-white">
            A live showcase of our reels, carousels and campaign creative — <em style={fraunces()}>dropping soon</em>.
          </div>
          <p className="v2-lede text-[#B8B4A6] mt-4">
            We&rsquo;re curating the strongest recent work into a proper in-motion showcase. In the meantime, our creative and social output lives inside each case study.
          </p>
        </div>
        <a href="/work" className="v2-mono text-[11px] uppercase tracking-[0.24em] text-white hover:text-[#E85D2F] transition-colors inline-flex items-center gap-2 whitespace-nowrap">
          See it in the work <ArrowUpRight size={13} />
        </a>
      </motion.div>
    </section>
  );
}

/* ================================================================
   06 · PERFORMANCE THESIS (paid growth)
   ================================================================ */
function PerformanceThesis() {
  const stages = [
    { n: '01', h: 'Traffic',           p: 'Meta, Google, retargeting. The tap you can turn up or down.',       col: '#B8B4A6' },
    { n: '02', h: 'Qualified leads',   p: 'Landing pages built to convert the traffic — not just receive it.', col: '#F5F1E8' },
    { n: '03', h: 'Customers',         p: 'Follow-up automation, CRM, and the sales tools your team actually uses.', col: '#34D399' },
    { n: '04', h: 'Revenue',           p: 'Attributed properly. CPL, CAC and ROAS — not vanity impressions.',   col: '#E85D2F' },
  ];
  return (
    <section className="border-t border-white/[0.08] py-24 md:py-32 px-6 md:px-12 lg:px-20 max-w-[1600px] mx-auto" data-testid="v2-home-performance">
      <div className="mb-14 max-w-4xl">
        <motion.div {...REVEAL}><V2Kicker num="06">Performance</V2Kicker></motion.div>
        <motion.h2 {...REVEAL} transition={{ ...REVEAL.transition, delay: 0.08 }} className="v2-h1 mt-4 max-w-[22ch]">
          We don&rsquo;t run ads for clicks.
          <br />
          We run them for <em style={fraunces()}>growth</em>.
        </motion.h2>
        <motion.p {...REVEAL} transition={{ ...REVEAL.transition, delay: 0.16 }} className="v2-lede mt-6 max-w-[62ch] text-[#B8B4A6]">
          The traffic → qualified leads → customers → revenue chain is one system, not four handoffs. We build it end-to-end — creative, landing pages, tracking, automation — so the numbers you see are the ones that matter.
        </motion.p>
      </div>

      <div className="grid md:grid-cols-4 gap-3 md:gap-2">
        {stages.map((s, i) => (
          <motion.div
            key={s.n}
            {...REVEAL}
            transition={{ ...REVEAL.transition, delay: 0.05 + i * 0.08 }}
            className="p-6 md:p-8 rounded-sm ring-1 ring-white/[0.08] relative overflow-hidden min-h-[220px] flex flex-col"
            style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.02) 0%, transparent 100%)' }}
          >
            <div className="flex items-center justify-between mb-6">
              <span className="v2-mono text-[10px] uppercase tracking-[0.28em] text-[#9C978A]">{s.n}</span>
              {i < 3 && <ArrowRight size={14} className="text-[#9C978A] hidden md:block" />}
            </div>
            <div className="v2-h2 text-[26px]" style={{ color: s.col }}>
              {i === 3 ? <em style={fraunces()}>{s.h}</em> : s.h}
            </div>
            <p className="mt-3 text-[14px] leading-relaxed text-[#B8B4A6] flex-1">{s.p}</p>
          </motion.div>
        ))}
      </div>

      <motion.div {...REVEAL} className="mt-10 flex flex-wrap gap-x-8 gap-y-3 v2-mono text-[10px] uppercase tracking-[0.24em] text-[#9C978A]">
        {['Meta Ads', 'Google Ads', 'Retargeting', 'Landing-page optimisation', 'Conversion tracking', 'Creative testing', 'CPL / CAC / ROAS'].map((t) => <span key={t}>· {t}</span>)}
      </motion.div>
    </section>
  );
}

/* ================================================================
   07 · TECHNOLOGY PROOF
   ================================================================ */
function TechnologyProof() {
  return (
    <section className="border-t border-white/[0.08] py-24 md:py-32 px-6 md:px-12 lg:px-20 max-w-[1600px] mx-auto" data-testid="v2-home-tech">
      <div className="grid md:grid-cols-2 gap-14 items-start">
        <div>
          <motion.div {...REVEAL}><V2Kicker num="07">Technology</V2Kicker></motion.div>
          <motion.h2 {...REVEAL} transition={{ ...REVEAL.transition, delay: 0.08 }} className="v2-h1 mt-4 max-w-[18ch]">
            We don&rsquo;t just do marketing.
            <br />
            We <em style={fraunces()}>build</em> the systems beneath it.
          </motion.h2>
          <motion.p {...REVEAL} transition={{ ...REVEAL.transition, delay: 0.2 }} className="v2-lede mt-6 max-w-[52ch] text-[#B8B4A6]">
            CRMs. Custom applications. Internal platforms. Automated workflows. AI-assisted operations. The reason our marketing outputs run smoother than most agencies&rsquo; is that we build the machinery too.
          </motion.p>
          <motion.ul {...REVEAL} transition={{ ...REVEAL.transition, delay: 0.28 }} className="mt-10 space-y-3">
            {[
              'Custom CRM platforms',
              'Business applications & SaaS',
              'Customer portals & dashboards',
              'AI workflow automation',
              'Complex integrations',
              'Internal ops platforms',
            ].map((t) => (
              <li key={t} className="flex items-start gap-3 text-[15px] text-[#CFCBBD]">
                <span className="v2-mono text-[10px] text-[#9C978A] tabular-nums pt-1.5">—</span>
                <span>{t}</span>
              </li>
            ))}
          </motion.ul>
        </div>

        {/* Mock CRM/App interface */}
        <motion.div {...REVEAL} transition={{ ...REVEAL.transition, delay: 0.12 }} className="relative aspect-[4/5] rounded-sm bg-[#0A0A0F] ring-1 ring-white/10 overflow-hidden">
          <div className="p-4 border-b border-white/5 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-white/20" />
            <span className="w-2 h-2 rounded-full bg-white/20" />
            <span className="w-2 h-2 rounded-full bg-white/20" />
            <span className="ml-3 v2-mono text-[9px] tracking-widest text-white/40 uppercase">brightridge.uk/admin/leads</span>
          </div>
          <div className="p-6">
            <div className="v2-mono text-[9px] uppercase tracking-[0.28em] text-[#9C978A]">CRM · Live pipeline</div>
            <div className="mt-2 v2-h2 text-[28px]" style={{ fontFamily: 'Fraunces', fontWeight: 300, fontStyle: 'italic', color: '#F5F1E8' }}>Brightridge Care</div>
            <div className="mt-6 grid grid-cols-4 gap-2">
              {[
                { l: 'New',      c: 42, col: '#B8B4A6' },
                { l: 'Contacted',c: 28, col: '#3B4DFF' },
                { l: 'Qualified',c: 14, col: '#34D399' },
                { l: 'Placed',   c: 7,  col: '#E85D2F' },
              ].map((s) => (
                <div key={s.l} className="p-3 rounded-sm bg-white/[0.03] ring-1 ring-white/5">
                  <div className="v2-mono text-[9px] uppercase tracking-widest text-[#9C978A]">{s.l}</div>
                  <div className="mt-2 text-2xl font-black text-white tabular-nums">{s.c}</div>
                  <div className="mt-2 h-0.5 rounded-full" style={{ background: s.col, width: `${Math.min(100, s.c * 2)}%` }} />
                </div>
              ))}
            </div>
            <div className="mt-6 space-y-2">
              {[
                { n: 'Sarah M.',    r: 'Homecare Assistant',    t: '2m ago',  col: '#34D399' },
                { n: 'Aditya P.',   r: 'RGN · Nights',           t: '11m ago', col: '#3B4DFF' },
                { n: 'Marcus J.',   r: 'Care Assistant',         t: '38m ago', col: '#B8B4A6' },
                { n: 'Priya S.',    r: 'Senior Care Assistant',  t: '1h ago',  col: '#B8B4A6' },
              ].map((l, i) => (
                <div key={i} className="flex items-center gap-3 px-3 py-2.5 rounded-sm bg-white/[0.02] ring-1 ring-white/5">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: l.col }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] text-white font-semibold truncate">{l.n}</div>
                    <div className="text-[11px] text-[#9C978A] truncate">{l.r}</div>
                  </div>
                  <div className="v2-mono text-[9px] text-[#9C978A] tabular-nums">{l.t}</div>
                </div>
              ))}
            </div>
            <div className="mt-6 pt-4 border-t border-white/5 flex items-center gap-2 v2-mono text-[9px] uppercase tracking-widest text-[#9C978A]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#34D399] animate-pulse" />
              <span>3 automations running · 12 today</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ================================================================
   08 · FEATURED CASE STUDY (SugaSpice — mocked editorial layout)
   ================================================================ */
function FeaturedCase() {
  return (
    <section className="border-t border-white/[0.08] py-24 md:py-32" data-testid="v2-home-featured" style={{ background: '#0A0A0F' }}>
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20">
        <motion.div {...REVEAL}><V2Kicker num="08">Featured project</V2Kicker></motion.div>
        <motion.h2 {...REVEAL} transition={{ ...REVEAL.transition, delay: 0.08 }} className="v2-display mt-6 max-w-[14ch]" style={{ fontSize: 'clamp(56px,9vw,160px)', color: '#E85D2F', fontFamily: 'Fraunces', fontWeight: 300, fontStyle: 'italic' }}>
          SugaSpice
        </motion.h2>
        <motion.div {...REVEAL} transition={{ ...REVEAL.transition, delay: 0.16 }} className="mt-8 grid md:grid-cols-3 gap-6 v2-mono text-[11px] uppercase tracking-[0.24em] text-[#B8B4A6]">
          <div><span className="text-white">Discipline · </span>Brand support · Web · Wholesale CRM</div>
          <div><span className="text-white">Sector · </span>Bakery &amp; Kitchen — St Neots</div>
          <div><span className="text-white">Year · </span>2025</div>
        </motion.div>

        <motion.div {...REVEAL} transition={{ ...REVEAL.transition, delay: 0.24 }} className="mt-14 grid md:grid-cols-[1.4fr_1fr] gap-10 items-start">
          <Link to="/work/sugaspice" className="group relative aspect-[16/10] rounded-sm bg-[#0F0F16] ring-1 ring-white/10 overflow-hidden block" data-testid="v2-home-featured-shot">
            <div className="absolute inset-0" style={{ background: 'radial-gradient(80% 60% at 20% 30%, rgba(232,93,47,0.25) 0%, transparent 60%), #0A0A0F' }} />
            <div className="absolute inset-6 md:inset-10 rounded-sm bg-[#07070C] ring-1 ring-white/10 overflow-hidden">
              <div className="flex items-center gap-1.5 px-3 py-2 border-b border-white/5">
                <span className="w-2 h-2 rounded-full bg-white/20" />
                <span className="w-2 h-2 rounded-full bg-white/20" />
                <span className="w-2 h-2 rounded-full bg-white/20" />
                <span className="ml-3 v2-mono text-[10px] text-white/40 tracking-wider">sugaspice.uk</span>
                <span className="ml-auto v2-mono text-[9px] tracking-wider inline-flex items-center gap-1.5" style={{ color: '#E85D2F' }}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#E85D2F', boxShadow: '0 0 6px #E85D2F' }} /> LIVE
                </span>
              </div>
              <img src="/case-shots/sugaspice.webp" alt="SugaSpice — live site" className="absolute inset-x-0 top-[36px] bottom-0 w-full h-[calc(100%-36px)] object-cover object-top transition-transform duration-[1200ms] group-hover:scale-[1.03]" draggable={false} />
            </div>
          </Link>
          <div>
            <div className="v2-mono text-[11px] uppercase tracking-[0.24em] text-[#9C978A] mb-3">The brief</div>
            <p className="v2-lede text-[#CFCBBD] max-w-[46ch]">A new, modern bakery &amp; kitchen opening in St Neots — needed a proper brand story, a website that could sell direct, and a wholesale operation that could scale without spreadsheets breaking.</p>
            <div className="v2-mono text-[11px] uppercase tracking-[0.24em] text-[#9C978A] mt-10 mb-3">What we built</div>
            <ul className="space-y-2 text-[15px] text-[#CFCBBD]">
              {['Brand support &amp; direction — informing the visual system and food photography direction', 'Website — six-category menu, direct online ordering, wholesale enquiry funnel', 'Custom Wholesale CRM — sale-or-return terms, refrigerated delivery routes, partner network', 'Local SEO — 14 menu-category landing pages ranking across St Neots &amp; Cambridgeshire'].map((t) => (
                <li key={t} className="flex items-start gap-2"><span className="v2-mono text-[#E85D2F]">—</span><span dangerouslySetInnerHTML={{ __html: t }} /></li>
              ))}
            </ul>
            <Link to="/work/sugaspice" className="mt-10 inline-flex items-center gap-2 v2-mono text-[12px] uppercase tracking-[0.24em] text-white hover:text-[#E85D2F] transition-colors" data-testid="v2-featured-cta">
              Read the case study <ArrowUpRight size={14} />
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ================================================================
   09 · HOW WE WORK
   ================================================================ */
function HowWeWork() {
  return (
    <section className="border-t border-white/[0.08] py-24 md:py-32 px-6 md:px-12 lg:px-20 max-w-[1600px] mx-auto" data-testid="v2-home-how">
      <div className="grid md:grid-cols-2 gap-14 items-start">
        <div className="md:sticky md:top-32">
          <motion.div {...REVEAL}><V2Kicker num="09">How we work</V2Kicker></motion.div>
          <motion.h2 {...REVEAL} transition={{ ...REVEAL.transition, delay: 0.08 }} className="v2-h1 mt-4 max-w-[16ch]">
            Small team.
            <br />
            <em style={fraunces()}>Serious</em> capability.
          </motion.h2>
          <motion.p {...REVEAL} transition={{ ...REVEAL.transition, delay: 0.16 }} className="v2-lede mt-6 max-w-[46ch] text-[#B8B4A6]">
            We keep our client roster deliberately small so every project gets the attention it deserves. We don&rsquo;t take every enquiry — only the ones where we believe we can make a difference.
          </motion.p>
        </div>

        <div className="space-y-6">
          {[
            { n: '01', h: 'Selective by design.',           p: 'We take on a controlled number of projects at a time. Fewer clients, deeper work, better outcomes.' },
            { n: '02', h: 'Senior everywhere.',              p: 'No junior account layers. No outsourced delivery chain. Norwich, London and Pune are all part of one BrightLabs team — the people who scope your work are the ones who ship it.' },
            { n: '03', h: 'Modern stack. Sharp delivery.',   p: 'AI in the workflow. Custom internal tooling. Small senior teams shipping faster than agencies four times our size.' },
            { n: '04', h: 'Commercially honest.',            p: 'If your website is the real problem, we&rsquo;ll say so before selling you social. Outcomes over tactics.' },
          ].map((c, i) => (
            <motion.div key={c.n} {...REVEAL} transition={{ ...REVEAL.transition, delay: 0.05 + i * 0.06 }} className="grid grid-cols-[60px_1fr] gap-6 pt-8 border-t border-white/[0.06]">
              <div className="v2-mono text-[10px] uppercase tracking-[0.28em] text-[#9C978A] pt-1">{c.n}</div>
              <div>
                <h3 className="v2-h3 text-white text-[22px]">{c.h}</h3>
                <p className="mt-3 text-[15px] text-[#B8B4A6] leading-relaxed max-w-[52ch]" dangerouslySetInnerHTML={{ __html: c.p }} />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ================================================================
   10 · WAYS TO WORK WITH US (starting investments — no giant table)
   ================================================================ */
function WaysToWork() {
  const rows = [
    { name: 'Websites & Digital Experiences', to: '/services/websites',         from: 'From £1,200',       note: 'One-off project. Scoped after discovery.' },
    { name: 'Creative, Social & Branding',    to: '/services/creative-social',  from: 'From £650/mo',      note: 'Ongoing. Rolling after 3-month minimum.' },
    { name: 'Paid Growth',                    to: '/services/paid-growth',      from: 'From £499/mo',      note: 'Plus ad spend, paid direct to platforms.' },
    { name: 'SEO & Organic Growth',           to: '/services/seo',              from: 'From £500/mo',      note: '3-month minimum. Compounding.' },
    { name: 'Software, Apps & Automation',    to: '/services/software',         from: 'From £1,800',       note: 'Scoped per project.' },
    { name: 'Growth Partnership',             to: '/services/growth-partnership', from: 'From £1,650/mo',  note: 'Your outsourced digital team — everything under one engagement.' },
  ];
  return (
    <section className="border-t border-black/[0.08] py-24 md:py-32" data-testid="v2-home-ways" style={{ background: V2_COLORS.paperCream, color: V2_COLORS.ink }}>
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20">
        <div className="mb-14 max-w-4xl">
          <motion.div {...REVEAL}><V2Kicker num="10" warm>Ways to work with us</V2Kicker></motion.div>
          <motion.h2 {...REVEAL} transition={{ ...REVEAL.transition, delay: 0.08 }} className="v2-h1 mt-4 max-w-[22ch]" style={{ color: V2_COLORS.ink }}>
            No one-size-fits-all.
            <br />
            <em style={fraunces()}>Real</em> scope. Real numbers.
          </motion.h2>
          <motion.p {...REVEAL} transition={{ ...REVEAL.transition, delay: 0.16 }} className="v2-lede mt-6 max-w-[58ch] text-[#3F3A2E]">
            Some clients need one website. Others need an entire growth function. We build the right scope around the outcome — starting investments below, bespoke scope after a proper discovery.
          </motion.p>
        </div>

        <div className="border-t border-black/[0.1]">
          {rows.map((r, i) => (
            <motion.div key={r.name} {...REVEAL} transition={{ ...REVEAL.transition, delay: 0.03 * i }}>
              <Link
                to={r.to}
                data-testid={`v2-home-ways-${r.to.split('/').pop()}`}
                className="grid md:grid-cols-[1.5fr_1fr_1.4fr_auto] gap-6 items-baseline py-8 border-b border-black/[0.1] group hover:bg-white/40 transition-colors -mx-3 md:-mx-6 px-3 md:px-6 rounded-sm"
              >
                <div className="v2-h2 text-[26px] md:text-[32px]" style={{ color: V2_COLORS.ink }}>{r.name}</div>
                <div className="v2-mono text-[12px] uppercase tracking-[0.22em]" style={{ color: V2_COLORS.warm }}>{r.from}</div>
                <div className="text-[15px] text-[#3F3A2E] max-w-[42ch]">{r.note}</div>
                <ArrowUpRight size={22} className="text-[#9C978A] group-hover:text-[#1A1A22] group-hover:rotate-45 transition-all duration-500" />
              </Link>
            </motion.div>
          ))}
        </div>

        <motion.p {...REVEAL} className="mt-10 v2-mono text-[11px] uppercase tracking-[0.22em] text-[#9C978A] max-w-[70ch]">
          Every engagement is scoped after a proper conversation — the numbers above are starting points, not final quotes.
        </motion.p>
      </div>
    </section>
  );
}

/* ================================================================
   11 · FINAL CTA
   ================================================================ */
function FinalCTA() {
  return (
    <section className="relative py-32 md:py-48 px-6 md:px-12 lg:px-20 max-w-[1600px] mx-auto" data-testid="v2-home-final-cta">
      <motion.h2
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="v2-display leading-[0.9] tracking-[-0.03em] max-w-[16ch]"
      >
        Tell us the{' '}
        <em style={fraunces()}>outcome</em>.
        <br />
        We&rsquo;ll help decide what actually needs building.
      </motion.h2>

      <motion.div {...REVEAL} className="mt-14 flex flex-wrap items-center gap-4">
        <PrimaryCTA>Start a Project</PrimaryCTA>
        <GhostCTA to="#work">See more work</GhostCTA>
      </motion.div>

      <motion.p {...REVEAL} transition={{ ...REVEAL.transition, delay: 0.2 }} className="mt-16 max-w-[60ch] text-[#B8B4A6] v2-lede">
        We keep our client roster deliberately controlled. Send us your brief — we&rsquo;ll come back if we believe we&rsquo;re the right team for it.
      </motion.p>
    </section>
  );
}

/* ----------------------------------------------------------------
   helpers
   ---------------------------------------------------------------- */
function fraunces(_word, gradient) {
  const base = {
    fontFamily: 'Fraunces, serif',
    fontOpticalSizing: 'auto',
    fontStyle: 'italic',
    fontWeight: 300,
  };
  if (!gradient) return base;
  return {
    ...base,
    backgroundImage: 'linear-gradient(92deg, #3B4DFF 0%, #7B3DFF 55%, #C939FF 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    color: 'transparent',
  };
}
